import { useMemo, useState } from "react";
import { Alert, Box, Button, Snackbar } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  useImports,
  useImportRerun,
  useUpdateImport,
} from "../../../api/queries";
import type { ImportDiffDto, ImportItem, ImportSort } from "../../../api/types";
import { ImportDetailsModal } from "../../../components/ImportDetailsModal";
import { ImportCsvModal } from "../../../components/ImportCsvModal";
import { ImportDiffModal } from "../../../components/ImportDiffModal";
import { ImportTransactionsModal } from "../../../components/ImportTransactionsModal";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import {
  ViewModeToggle,
  type ViewMode,
} from "../../../components/ViewModeToggle";
import {
  SortByDropdown,
  type SortByOption,
} from "../../../components/SortByDropdown";
import {
  AdminListBody,
  AdminListPageHeader,
} from "../../../components/AdminList";
import { filterImports } from "./filterImports";
import { defaultFilters, type Filters } from "./types";
import { ImportFilters } from "./components/ImportFilters";
import { ImportListView } from "./components/ImportListView";
import { ImportTileView } from "./components/ImportTileView";
import { NewImportModal } from "./components/NewImportModal";

const VIEW_MODE_KEY = "imports.viewMode";
const SORT_BY_KEY = "imports.sortBy";

const SORT_OPTIONS: ReadonlyArray<SortByOption<ImportSort>> = [
  { value: "uploadDateDesc", label: "Upload date" },
  { value: "firstTxDateDesc", label: "First transaction date" },
];

const readStoredViewMode = (): ViewMode => {
  try {
    const stored = window.localStorage?.getItem?.(VIEW_MODE_KEY);
    return stored === "tile" ? "tile" : "list";
  } catch {
    return "list";
  }
};

const writeStoredViewMode = (next: ViewMode) => {
  try {
    window.localStorage?.setItem?.(VIEW_MODE_KEY, next);
  } catch {
    // ignore — SSR, private mode, or test env without storage
  }
};

const readStoredSortBy = (): ImportSort => {
  try {
    const stored = window.localStorage?.getItem?.(SORT_BY_KEY);
    return stored === "firstTxDateDesc" ? "firstTxDateDesc" : "uploadDateDesc";
  } catch {
    return "uploadDateDesc";
  }
};

const writeStoredSortBy = (next: ImportSort) => {
  try {
    window.localStorage?.setItem?.(SORT_BY_KEY, next);
  } catch {
    // ignore — SSR, private mode, or test env without storage
  }
};

export const ImportsPage = () => {
  const updateImport = useUpdateImport();
  const importRerun = useImportRerun();

  const [editingImport, setEditingImport] = useState<ImportItem | null>(null);
  const [viewingCsvImport, setViewingCsvImport] = useState<ImportItem | null>(
    null,
  );
  const [diffImport, setDiffImport] = useState<ImportItem | null>(null);
  const [preloadedDiff, setPreloadedDiff] = useState<ImportDiffDto | null>(
    null,
  );
  const [txImport, setTxImport] = useState<ImportItem | null>(null);
  const [deletingImport, setDeletingImport] = useState<ImportItem | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    readStoredViewMode(),
  );
  const [sortBy, setSortBy] = useState<ImportSort>(() => readStoredSortBy());
  const [newImportOpenedAt, setNewImportOpenedAt] = useState<number | null>(
    null,
  );

  const handleViewModeChange = (next: ViewMode) => {
    setViewMode(next);
    writeStoredViewMode(next);
  };

  const handleSortByChange = (next: ImportSort) => {
    setSortBy(next);
    writeStoredSortBy(next);
  };

  const { data: imports, isPending } = useImports(sortBy);

  const importerOptions = useMemo(() => {
    if (!imports) return [];
    const seen = new Set<string>();
    for (const item of imports) seen.add(item.importerName);
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [imports]);

  const filtered = useMemo(
    () => (imports ? filterImports(imports, filters) : []),
    [imports, filters],
  );

  const total = imports?.length ?? 0;

  const handleConfirmDelete = () => {
    if (!deletingImport) return;
    const id = deletingImport.id;
    setDeletingImport(null);
    updateImport.mutate(
      { importId: id, body: { deleted: true } },
      { onError: () => setSnackbar("Failed to delete import.") },
    );
  };

  const handleRestore = (item: ImportItem) => {
    updateImport.mutate(
      { importId: item.id, body: { deleted: false } },
      { onError: () => setSnackbar("Failed to restore import.") },
    );
  };

  // Re-open a period finalized from its invoice. The transactions stay; only
  // the lock that refuses statement uploads is cleared.
  const handleClearFinalized = (item: ImportItem) => {
    updateImport.mutate(
      { importId: item.id, body: { finalized: false } },
      { onError: () => setSnackbar("Failed to clear the finalized state.") },
    );
  };

  const handleRerun = (item: ImportItem) => {
    importRerun.mutate(item.id, {
      onSuccess: (diff) => {
        setPreloadedDiff(diff);
        setDiffImport(item);
      },
      onError: (e) =>
        setSnackbar(
          `Re-run failed: ${e instanceof Error ? e.message : String(e)}`,
        ),
    });
  };

  const rerunningId = importRerun.isPending
    ? (importRerun.variables ?? null)
    : null;

  const closeDiffModal = () => {
    setDiffImport(null);
    setPreloadedDiff(null);
  };

  return (
    <Box sx={{ p: 2 }}>
      <AdminListPageHeader
        title="Imports"
        actions={
          <>
            <SortByDropdown
              value={sortBy}
              onChange={handleSortByChange}
              options={SORT_OPTIONS}
              labelId="import-sort-by"
            />
            <ViewModeToggle
              value={viewMode}
              onChange={handleViewModeChange}
              ariaLabel="imports view mode"
            />
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => setNewImportOpenedAt(Date.now())}
            >
              New import
            </Button>
          </>
        }
      />

      <ImportFilters
        value={filters}
        onChange={setFilters}
        importerOptions={importerOptions}
      />

      <AdminListBody
        isLoading={isPending}
        total={total}
        filteredCount={filtered.length}
        noun="import"
      >
        {viewMode === "list" ? (
          <ImportListView
            imports={filtered}
            onEdit={setEditingImport}
            onViewTransactions={setTxImport}
            onViewCsv={setViewingCsvImport}
            onOpenDiff={setDiffImport}
            onRerun={handleRerun}
            rerunningId={rerunningId}
            onDelete={setDeletingImport}
            onRestore={handleRestore}
            onClearFinalized={handleClearFinalized}
          />
        ) : (
          <ImportTileView
            imports={filtered}
            sortBy={sortBy}
            onEdit={setEditingImport}
            onViewTransactions={setTxImport}
            onViewCsv={setViewingCsvImport}
            onOpenDiff={setDiffImport}
            onRerun={handleRerun}
            rerunningId={rerunningId}
            onDelete={setDeletingImport}
            onRestore={handleRestore}
            onClearFinalized={handleClearFinalized}
          />
        )}
      </AdminListBody>

      {editingImport && (
        <ImportDetailsModal
          key={editingImport.id}
          open
          onClose={() => setEditingImport(null)}
          importItem={editingImport}
        />
      )}
      <ImportCsvModal
        open={!!viewingCsvImport}
        onClose={() => setViewingCsvImport(null)}
        importItem={viewingCsvImport}
      />
      {diffImport && (
        <ImportDiffModal
          open
          onClose={closeDiffModal}
          importId={diffImport.id}
          preloadedDiff={preloadedDiff}
        />
      )}
      {txImport && (
        <ImportTransactionsModal
          importItem={txImport}
          onClose={() => setTxImport(null)}
        />
      )}
      <NewImportModal
        key={newImportOpenedAt ?? "closed"}
        open={newImportOpenedAt !== null}
        onClose={() => setNewImportOpenedAt(null)}
      />
      <ConfirmDialog
        open={!!deletingImport}
        title="Delete import?"
        message={
          deletingImport
            ? `The import "${deletingImport.importerName}" and its transactions will be hidden. You can restore it later from the Deleted view.`
            : ""
        }
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeletingImport(null)}
      />
      <Snackbar
        open={snackbar !== null}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => setSnackbar(null)}
          sx={{ width: "100%" }}
        >
          {snackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
};
